import React, { useEffect, useMemo, useState } from "react";
import { signalsApi, intelApi } from "../../lib/api";
import { Card } from "../shared";
import { Radar, ExternalLink, Plus, Globe, TrendingUp, Building2, Landmark, Map, DollarSign, AlertTriangle, Sparkles, Bookmark, Trash2 } from "lucide-react";
import { toast } from "sonner";

const KINDS = {
  consolidation: { label: "Consolidation", icon: TrendingUp },
  thesis: { label: "Thesis forming", icon: Sparkles },
  whitespace: { label: "White space", icon: Map },
  players: { label: "Big players", icon: Building2 },
  capital: { label: "Capital & debt", icon: Landmark },
  pricing: { label: "Pricing", icon: DollarSign },
  risk: { label: "Risk", icon: AlertTriangle },
};
const LENDER_TYPES = ["bank", "credit fund", "mezzanine", "sba", "lender"];
const band = (n) => (n >= 80 ? "hot" : n >= 60 ? "warm" : n >= 40 ? "cool" : "cold");

const Player = ({ p, sid, i }) => {
  const isLender = LENDER_TYPES.some((t) => (p.type || "").toLowerCase().includes(t));
  const add = async (audience) => {
    try {
      const res = await intelApi.promote({ audience, rows: [{ firm: p.name, name: p.name, type: p.type || "", thesis: p.note || "", why: p.note || "" }] });
      toast.success(res.added.length ? `${p.name} added to ${audience === "lenders" ? "Debt Lenders" : "Investors"}` : `${p.name} is already in the CRM`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add them"); }
  };
  return (
    <div className="sig-player" data-testid={`signal-player-${sid}-${i}`}>
      <b>{p.name}</b>
      {p.type ? <em>{p.type}</em> : null}
      {p.note ? <span>{p.note}</span> : null}
      <span className="sig-player-acts">
        <a href={`https://www.google.com/search?q=${encodeURIComponent(p.name)}`} target="_blank" rel="noreferrer" title={`Search the web for ${p.name}`}><Globe size={11} /></a>
        <button onClick={() => add(isLender ? "lenders" : "investors")} data-testid={`signal-add-${sid}-${i}`}
          title={`Add ${p.name} to ${isLender ? "Debt Lenders" : "Investors"}`}><Plus size={11} />{isLender ? "Lenders" : "Investors"}</button>
      </span>
    </div>
  );
};

export const SignalsPanel = ({ dealKey, dealName, industry, geography }) => {
  const [ctx, setCtx] = useState({ industry: industry || "", geography: geography || "", thesis: "", notes: "" });
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const [kind, setKind] = useState("all");
  const [saved, setSaved] = useState([]);
  const [savedId, setSavedId] = useState("");

  useEffect(() => { setCtx((c) => ({ ...c, industry: c.industry || industry || "", geography: c.geography || geography || "" })); }, [industry, geography]);
  const loadSaved = () => signalsApi.searches().then((r) => setSaved(r.searches || [])).catch(() => setSaved([]));
  useEffect(() => { loadSaved(); }, [dealKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    signalsApi.last({ dealKey, dealName }).then((r) => {
      setRun(r && r.id ? r : null);
      if (r?.status === "running") setBusy(true);
    }).catch(() => setRun(null));
  }, [dealKey, dealName]);

  useEffect(() => {
    if (!busy || !run?.id) return;
    const t = setInterval(async () => {
      try {
        const r = await signalsApi.run(run.id);
        setRun(r);
        if (r.status !== "running") {
          setBusy(false);
          if (r.status === "error") toast.error(r.error || "The scan stopped early — partial findings are shown");
          else toast.success(`${r.signals.length} market signals surfaced`);
        }
      } catch { /* keep polling */ }
    }, 3000);
    return () => clearInterval(t);
  }, [busy, run?.id]);

  const scan = async () => {
    setBusy(true);
    try {
      setRun(await signalsApi.scan({ ...ctx, dealKey, dealName }));
      toast.success("Scouting the web for market signals…");
    } catch (e) { toast.error(e?.response?.data?.detail || "Scan failed"); setBusy(false); }
  };

  const saveScan = async () => {
    const name = window.prompt("Name this scan", `${ctx.industry || "Market"} — ${ctx.geography || "US"}`.trim());
    if (name === null) return;
    try {
      await signalsApi.saveSearch({ name, runId: run?.id || "", context: { ...ctx, dealKey, dealName } });
      await loadSaved();
      toast.success(`Saved “${name}” — reload it any time from the dropdown`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save the scan"); }
  };

  const loadScan = async (id) => {
    const hit = saved.find((x) => x.id === id);
    if (!hit) return;
    setCtx({ industry: hit.context.industry || "", geography: hit.context.geography || "",
      thesis: hit.context.thesis || "", notes: hit.context.notes || "" });
    setKind("all");
    setSavedId(id);
    if (hit.runId) {
      try {
        const r = await signalsApi.run(hit.runId);
        setRun(r);
        if (r.status === "running") setBusy(true);
      } catch { toast.error("The saved findings are no longer available — run the scan again"); }
    }
    toast.success(`Loaded “${hit.name}”`);
  };

  const removeScan = async (id) => {
    try { await signalsApi.deleteSearch(id); setSavedId(""); await loadSaved(); toast.success("Saved scan removed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove it"); }
  };

  const signals = useMemo(() => (run?.signals || []).filter((s) => kind === "all" || s.kind === kind), [run, kind]);
  const counts = useMemo(() => {
    const c = {};
    (run?.signals || []).forEach((s) => { c[s.kind] = (c[s.kind] || 0) + 1; });
    return c;
  }, [run]);

  return (
    <>
      <Card title="Market Scan" testid="signals-input" right={(
        <button className="btn btn-primary" onClick={scan} disabled={busy} data-testid="signals-run">
          <Radar size={13} style={{ marginRight: 6 }} />{busy ? "Scanning…" : "Scan the market"}
        </button>
      )}>
        <div className="intel-grid">
          <div className="field"><label>Industry</label><input className="inp inp-text" value={ctx.industry} onChange={(e) => setCtx({ ...ctx, industry: e.target.value })} data-testid="signals-industry" /></div>
          <div className="field"><label>Geography</label><input className="inp inp-text" value={ctx.geography} onChange={(e) => setCtx({ ...ctx, geography: e.target.value })} placeholder="United States" data-testid="signals-geo" /></div>
          <div className="field" style={{ gridColumn: "span 2" }}><label>Thesis / angle we care about</label>
            <input className="inp inp-text" value={ctx.thesis} onChange={(e) => setCtx({ ...ctx, thesis: e.target.value })}
              placeholder="e.g. regional density in grounds maintenance, recurring commercial contracts" data-testid="signals-thesis" /></div>
        </div>
        <div className="field" style={{ marginTop: 10 }}><label>Anything else the scout should dig into</label>
          <textarea className="inp inp-text" rows={2} value={ctx.notes} onChange={(e) => setCtx({ ...ctx, notes: e.target.value })}
            placeholder="e.g. who is buying two-man landscaping shops in Texas, and is anyone rolling up irrigation yet" data-testid="signals-notes" />
        </div>
        <div className="sig-actions">
          <button className="btn" onClick={saveScan} data-testid="signals-save-search"><Bookmark size={13} style={{ marginRight: 5 }} />Save this scan</button>
          {saved.length ? (
            <>
              <select className="inp" style={{ width: 220 }} value={savedId} onChange={(e) => e.target.value && loadScan(e.target.value)} data-testid="signals-saved-list">
                <option value="">Saved scans ({saved.length})</option>
                {saved.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
              </select>
              <button className="btn btn-sm" disabled={!savedId} onClick={() => removeScan(savedId)} title="Remove the selected scan" data-testid="signals-delete-search"><Trash2 size={12} /></button>
            </>
          ) : null}
        </div>
        <div className="sig-meta">
          {busy && run ? (
            <span className="ir-fine" data-testid="signals-progress">
              {run.phase === "ranking" ? "Ranking what is worth pursuing…" : `Scanning the web — ${run.themesDone || 0}/${run.themes || 7} themes · ${(run.signals || []).length} signals`}
            </span>
          ) : null}
          {run && !busy ? <span className="ir-fine" data-testid="signals-meta">Last scan {String(run.ranAt || "").slice(0, 16).replace("T", " ")} · {(run.signals || []).length} signals</span> : null}
        </div>
      </Card>

      {run?.read ? (
        <Card title="The market read" style={{ marginTop: 12 }} testid="signals-read">
          <div className="sig-read">{run.read}</div>
        </Card>
      ) : null}

      <Card title={`Signal${signals.length ? ` · ${signals.length}` : ""}`} style={{ marginTop: 12 }} testid="signals-list" right={(
        <div className="sig-filter" data-testid="signals-filter">
          <button className={kind === "all" ? "active" : ""} onClick={() => setKind("all")} data-testid="signals-kind-all">All</button>
          {Object.entries(KINDS).filter(([k]) => counts[k]).map(([k, v]) => (
            <button key={k} className={kind === k ? "active" : ""} onClick={() => setKind(k)} data-testid={`signals-kind-${k}`}>{v.label} {counts[k]}</button>
          ))}
        </div>
      )}>
        {!signals.length
          ? <div className="docs-empty" data-testid="signals-empty">{run?.status === "error" ? run.error : busy ? "The scout is reading the web — findings land here as each theme completes." : "Nothing scanned yet — set the industry above and hit “Scan the market”."}</div>
          : signals.map((s) => {
            const Icon = (KINDS[s.kind] || KINDS.thesis).icon;
            return (
              <div className={`sig-card ${band(s.score)}`} key={s.id} data-testid={`signal-${s.id}`}>
                <div className="sig-score" data-testid={`signal-score-${s.id}`}><b>{s.score}</b><span>worth it</span></div>
                <div className="sig-body">
                  <div className="sig-head">
                    <span className="sig-kind"><Icon size={11} /> {(KINDS[s.kind] || {}).label || s.kind}</span>
                    <b>{s.headline}</b>
                    {s.metric ? <em>{s.metric}</em> : null}
                  </div>
                  <div className="sig-detail">{s.detail}</div>
                  {s.whyNow ? <div className="sig-now"><Sparkles size={11} /> {s.whyNow}</div> : null}
                  {s.action ? <div className="sig-action"><span>Do this</span>{s.action}</div> : null}
                  {(s.players || []).length ? (
                    <div className="sig-players" data-testid={`signal-players-${s.id}`}>
                      {s.players.map((p, i) => <Player key={i} p={p} sid={s.id} i={i} />)}
                    </div>
                  ) : null}
                  {(s.sources || []).length ? (
                    <div className="intel-sources">
                      {s.sources.map((u, i) => <a key={i} href={u} target="_blank" rel="noreferrer">{`source ${i + 1}`}<ExternalLink size={9} /></a>)}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
      </Card>
    </>
  );
};
